import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useRates } from '../api/queries';
import { useSync } from '../sync/SyncProvider';
import { formatRate, initials, roleLabel } from '../lib/format';
import { BellIcon, DensityIcon, MenuIcon, SearchIcon } from './icons';

export function Topbar({ onMenu }: { onMenu: () => void }) {
  const { user } = useAuth();
  const { data: rates } = useRates();
  const { pending } = useSync();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [density, setDensity] = useState<'comfortable' | 'compact'>(() => {
    try {
      return window.localStorage.getItem('gravenca.table-density') === 'compact'
        ? 'compact'
        : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.documentElement.dataset.density = density;
    try {
      window.localStorage.setItem('gravenca.table-density', density);
    } catch {
      // Preferencia no persistente si el navegador bloquea el almacenamiento.
    }
  }, [density]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const usd = rates?.find((rate) => rate.moneda === 'USD');
  const eur = rates?.find((rate) => rate.moneda === 'EUR');

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const term = search.trim();
    navigate(term ? `/pedidos?search=${encodeURIComponent(term)}` : '/pedidos');
  };

  return (
    <header className="topbar">
      <button
        type="button"
        className="icon-button topbar__menu"
        onClick={onMenu}
        aria-label="Abrir menú de navegación"
      >
        <MenuIcon size={20} />
      </button>

      <div className="branch-select branch-select--fixed" title="Empresa única: GRAVENCA, C.A.">
        <span className="branch-select__logo" aria-hidden="true">
          G
        </span>
        <span className="branch-select__text">
          <strong>{user?.empresaNombre ?? 'GRAVENCA, C.A.'}</strong>
          <span>{user?.empresaRif ?? 'J-508362438'} · Sede Principal</span>
        </span>
      </div>

      <form className="topbar__search" onSubmit={submitSearch} role="search">
        <SearchIcon size={17} />
        <input
          ref={inputRef}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar módulo o cliente…"
          aria-label="Buscar en el ERP"
        />
        <kbd>⌘K</kbd>
      </form>

      <div className="topbar__right">
        <div className="ticker" aria-label="Tasas de cambio del día">
          <span className="rate-pill" title={usd ? `Fuente ${usd.fuente}` : 'Tasa no disponible'}>
            <span className="rate-pill__label">Bs/USD</span>
            <strong>{usd ? formatRate(usd.tasa) : '—'}</strong>
          </span>
          <span className="rate-pill" title={eur ? `Fuente ${eur.fuente}` : 'Tasa no disponible'}>
            <span className="rate-pill__label">Bs/EUR</span>
            <strong>{eur ? formatRate(eur.tasa) : '—'}</strong>
          </span>
        </div>

        <button
          type="button"
          className="density-control"
          aria-pressed={density === 'compact'}
          title={density === 'compact' ? 'Tablas compactas' : 'Tablas cómodas'}
          onClick={() => setDensity((current) => (current === 'compact' ? 'comfortable' : 'compact'))}
        >
          <DensityIcon size={15} />
          <span>{density === 'compact' ? 'Compacta' : 'Cómoda'}</span>
        </button>

        <button
          type="button"
          className="icon-button notification-button"
          aria-label={`Notificaciones${pending > 0 ? `: ${pending} pendientes` : ''}`}
          title={pending > 0 ? `${pending} movimiento(s) en cola` : 'Sin pendientes'}
        >
          <BellIcon size={19} />
          {pending > 0 ? <span className="notification-button__dot" aria-hidden="true" /> : null}
        </button>

        <div className="profile-chip" title={user?.email ?? ''}>
          <span className="avatar" aria-hidden="true">
            {user ? initials(user.nombre) : 'GV'}
          </span>
          <span className="profile-chip__text">
            <strong>{user?.nombre ?? 'Invitado'}</strong>
            <span>{user ? roleLabel(user.rol) : 'Sin sesión'}</span>
          </span>
        </div>
      </div>
    </header>
  );
}
